// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from 'react'
import * as d3 from 'd3'
import { rounded_rect } from '../../utils/graphUtils'
import { convertEpochToDate } from '../../utils/utils'
import { lightImpactVibrate } from '../../utils/appBridge'
import { EliteScoreTrends } from '../../types/mutual-fund-detail.types'
import { performanceTagMetaData } from '../../constants'

type PropTypes = {
  data: {
    month: number
    value: number
    label: string
    tag: `${EliteScoreTrends}`
    isVisible: boolean
  }[]
  showLabels: boolean
  onRankChange: Function
  categoryCount: number
  ranksType: 'ROLLING' | 'TRAILING'
}

const getPerformanceTagMetaData = (tag: `${EliteScoreTrends}`) => {
  return performanceTagMetaData[tag] ?? { label: '' }
}

const getPerformanceTagLabel = (tag: `${EliteScoreTrends}`) => getPerformanceTagMetaData(tag)?.label
const getPerformanceTagFromLabel = (label: string) =>
  Object?.keys(performanceTagMetaData)?.filter((key) => performanceTagMetaData?.[key]?.label === label)?.[0]
const getPerformanceTagColor = (tag: `${EliteScoreTrends}`) => getPerformanceTagMetaData(tag)?.color
const getPerformanceTagTextColor = (tag: `${EliteScoreTrends}`) => getPerformanceTagMetaData(tag)?.textColor

const NewRankGraph = ({ data, showLabels, onRankChange, categoryCount, ranksType }: PropTypes) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const topSVGRef = useRef<SVGSVGElement>(null)
  const lastDataRef = useRef<PropTypes['data'][0]>()
  const [width, setWidth] = useState(window.innerWidth)
  const height = 217

  const graphForm = useMemo(() => {
    return data?.slice(-1)?.[0]?.tag
  }, [data])

  useEffect(() => {
    const handleResize = () => {
      const parentWidth = (svgRef.current?.parentNode as HTMLElement)?.clientWidth ?? 0
      const parentHeight = (svgRef.current?.parentNode as HTMLElement)?.clientHeight ?? 250
      setWidth(parentWidth)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const margin = { top: 0, right: 0, bottom: 30, left: 0 }
    const chartWidth = width - margin.left - margin.right
    const chartHeight = height - margin.top - margin.bottom

    const topSVG = d3
      .select(topSVGRef.current)
      .attr('width', chartWidth + margin.left + margin.right)
      .attr('height', 33)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const svg = d3
      .select(svgRef.current)
      .attr('width', chartWidth + margin.left + margin.right)
      .attr('height', chartHeight + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    let graphStartWidth = 85
    if (data?.length > 7) graphStartWidth = 95
    if (data?.length > 13) graphStartWidth = 100

    const xScale = d3
      .scalePoint()
      .domain(data?.map((d) => d.month))
      .range([graphStartWidth, chartWidth - 15])
      .padding(0.5)

    const minValue = 1
    const maxValue = categoryCount ?? d3.max(data, (d) => d.value)
    const lastValue = data?.slice(-1)?.[0]?.value
    const lastMonth = data?.slice(-1)?.[0]?.month
    onRankChange(lastValue, 'Today', false)

    const yScale = d3
      .scaleLinear()
      .domain([maxValue + 2, minValue - 2])
      .range([chartHeight, 0])

    const powerUpLogoSvg = `<svg width="55" height="11" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M.87 8V.025h3.576c.642 0 1.18.134 1.615.401.444.26.772.607.986 1.043.221.435.332.913.332 1.432 0 .535-.11 1.024-.332 1.467A2.516 2.516 0 0 1 6.05 5.41c-.443.26-.978.39-1.604.39H2.52V8H.87Zm3.358-3.575c.497 0 .871-.134 1.123-.401s.378-.642.378-1.123-.13-.852-.39-1.111c-.252-.26-.622-.39-1.11-.39H2.52v3.025h1.707Zm6.799 3.712c-.627 0-1.177-.122-1.65-.366a2.71 2.71 0 0 1-1.1-1.054c-.253-.451-.379-.97-.379-1.559 0-.596.126-1.119.379-1.57.26-.45.622-.798 1.088-1.042.474-.245 1.027-.367 1.662-.367.634 0 1.184.122 1.65.367.473.244.836.592 1.088 1.043.26.45.39.974.39 1.57 0 .588-.13 1.107-.39 1.558-.252.45-.615.802-1.088 1.054-.474.244-1.024.366-1.65.366Zm0-1.191c.511 0 .916-.16 1.214-.481.298-.329.447-.764.447-1.307 0-.542-.149-.977-.447-1.306-.298-.328-.703-.493-1.214-.493-.512 0-.917.165-1.215.493-.298.329-.447.764-.447 1.306 0 .543.149.978.447 1.307.298.32.703.48 1.215.48ZM16.296 8 14.37 2.317h1.49l.973 3.15c.123.383.23.868.321 1.456l.012.092h.08c0-.07.023-.256.069-.562.053-.313.133-.642.24-.985l.951-3.128h1.284l.95 3.128c.108.343.184.672.23.985.053.306.08.493.08.562h.08l.012-.092a9.882 9.882 0 0 1 .32-1.455l.975-3.151h1.49L22.001 8h-1.787l-.734-2.59a7.864 7.864 0 0 1-.217-.996 6.362 6.362 0 0 1-.07-.562h-.09c0 .061-.024.248-.07.562a7.864 7.864 0 0 1-.217.996L18.083 8h-1.787Zm7.845-2.853c0-.611.126-1.138.378-1.581.26-.451.626-.795 1.1-1.032.473-.236 1.023-.355 1.65-.355.664 0 1.222.13 1.673.39.45.26.786.615 1.008 1.065.222.443.332.94.332 1.49 0 .13-.007.286-.023.47h-5.362l.779-.63c-.1.664-.008 1.168.275 1.512.29.336.726.504 1.306.504.374 0 .684-.072.928-.218.252-.145.428-.328.527-.55h1.513a2.28 2.28 0 0 1-.951 1.41c-.497.344-1.157.515-1.982.515-.986 0-1.757-.255-2.315-.767-.558-.52-.836-1.26-.836-2.223Zm1.5.023-.744-.539h3.93c0-.39-.134-.71-.401-.962-.267-.26-.653-.39-1.157-.39-.58 0-1.012.176-1.295.527-.283.352-.393.806-.332 1.364ZM31.449 8V2.317h1.42l-.24 1.272h.115c.19-.466.474-.806.848-1.02.382-.214.806-.321 1.272-.321.26 0 .527.034.802.103v1.33a3.679 3.679 0 0 0-.894-.104c-.642 0-1.111.16-1.41.481-.29.314-.435.791-.435 1.433V8h-1.478Zm8.675.137c-1.1 0-1.974-.297-2.623-.893-.642-.596-.963-1.452-.963-2.567V.025h1.662v4.652c0 .688.156 1.207.47 1.558.32.352.805.527 1.455.527.649 0 1.13-.175 1.443-.527.321-.351.481-.87.481-1.558V.025h1.662v4.652c0 1.115-.325 1.97-.974 2.567-.642.596-1.512.893-2.612.893Zm5.097 1.971V8h1.466v2.108H45.22Zm3.506-1.97a2.47 2.47 0 0 1-.997-.195 2.373 2.373 0 0 1-.733-.527 2.146 2.146 0 0 1-.424-.7h-.103L46.686 8H45.22V2.317h1.466L46.457 3.6h.115a2.17 2.17 0 0 1 .779-.997c.382-.283.84-.424 1.375-.424.496 0 .95.134 1.363.401.413.268.738.634.974 1.1.237.466.356.982.356 1.547s-.119 1.07-.356 1.513a2.588 2.588 0 0 1-.974 1.03c-.404.245-.859.367-1.363.367Zm-.413-1.204c.306 0 .58-.068.825-.206a1.57 1.57 0 0 0 .596-.607c.145-.26.218-.558.218-.894 0-.336-.073-.634-.218-.894a1.491 1.491 0 0 0-.596-.596 1.585 1.585 0 0 0-.825-.217c-.305 0-.58.072-.825.217a1.46 1.46 0 0 0-.561.596c-.138.26-.206.558-.206.894 0 .336.068.634.206.894.137.26.325.462.561.607.245.138.52.206.825.206Z" fill="#D1D6D6"/>
  <circle cx="53.5" cy="1.5" r="1.5" fill="#DDDFDF"/>
</svg>
`
    svg
      .append('g')
      .attr('transform', `translate(15, ${chartHeight + 10})`)
      .html(powerUpLogoSvg)

    const colorRanges = [
      getPerformanceTagColor(EliteScoreTrends.BAD),
      getPerformanceTagColor(EliteScoreTrends.AVERAGE),
      getPerformanceTagColor(EliteScoreTrends.GOOD),
      getPerformanceTagColor(EliteScoreTrends.GREAT)
    ]
    colorRanges.reverse()
    const numOfVerticalSections = colorRanges.length
    const sectionHeight = chartHeight / numOfVerticalSections

    colorRanges.forEach((color, i) => {
      svg
        .append('path')
        .attr('d', (d) => {
          return rounded_rect(0, i * sectionHeight, chartWidth - 1, sectionHeight, 11.5, i == 0, i == 0, false, false)
        })
        .attr('fill', color)
    })

    const numSections = 7
    const sectionedData = Array.from({ length: 7 }, (_, i) => data[Math.floor((i * (data.length - 1)) / 6)])

    svg
      .selectAll('line.vertical-line')
      .data(sectionedData)
      .enter()
      .append('line')
      .attr('class', 'vertical-line')
      .attr('x1', (d) => xScale(d.month))
      .attr('x2', (d) => xScale(d.month))
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', 'white')
      .attr('opacity', 0.5)
      .attr('stroke-width', 1)

    svg
      .append('g')
      .attr('transform', `translate(0,${chartHeight})`)
      .style('font-family', 'Matter')
      .call(
        d3
          .axisBottom(xScale)
          .tickSize(0)
          .tickPadding(10)
          .tickValues(sectionedData.map((d) => d.month))
          .tickFormat((d, i) =>
            ranksType === 'TRAILING' && i === sectionedData.length - 1 ? 'Today' : convertEpochToDate(d, '%b')
          )
      )
      .selectAll('text')
      .attr('font-weight', (d, i, nodes) => (i === sectionedData.length - 1 ? 700 : 400))
      .attr('fill', (d, i, nodes) => (i === sectionedData.length - 1 ? '#000000' : '#6A6E6E'))
      .select('.domain')
      .remove()

    svg.select('.domain').remove()

    const yLabels = [
      getPerformanceTagLabel(EliteScoreTrends.BAD),
      getPerformanceTagLabel(EliteScoreTrends.AVERAGE),
      getPerformanceTagLabel(EliteScoreTrends.GOOD),
      getPerformanceTagLabel(EliteScoreTrends.GREAT)
    ]
    yLabels.reverse()

    svg
      .selectAll('.y-label')
      .data(yLabels)
      .enter()
      .append('foreignObject')
      .attr('class', 'y-label')
      .attr('x', 10)
      .attr('y', (d, i) => sectionHeight * (i + 0.28))
      .attr('width', chartWidth - 10)
      .attr('height', 20)
      .html(
        (d) => `<div style="color: ${getPerformanceTagTextColor(getPerformanceTagFromLabel(d))}; font-size: 11px; 
        font-weight: ${graphForm === getPerformanceTagFromLabel(d) ? 700 : 500}; 
        display: flex; gap: 4px;">
      <div> ${d} </div> 
      </div>`
      )

    const line = d3
      .line()
      .x((d) => xScale(d.month))
      .y((d) => yScale(d.value))

    const filteredData = data?.filter((d) => d?.isVisible)

    svg
      .append('path')
      .datum(filteredData)
      .attr('fill', 'none')
      .attr('stroke', '#4B1600')
      .attr('stroke-width', 2)
      .attr('d', line)

    const topDragLine = topSVG
      .append('line')
      .attr('stroke', '#000000')
      .attr('stroke-width', 1.5)
      .attr('y1', 0)
      .attr('y2', 8)
      .style('visibility', 'hidden')

    const dragLine = svg
      .append('line')
      .attr('stroke', '#000000')
      .attr('stroke-width', 1.5)
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .style('visibility', 'hidden')

    const monthTooltip = topSVG
      .append('g')
      .style('visibility', 'hidden')
      .append('rect')
      .attr('width', 60)
      .attr('height', 24)
      .attr('rx', 8)
      .attr('ry', 8)
      .attr('fill', '#D8E2F9')
      .attr('y', 0)

    const monthText = topSVG
      .append('g')
      .style('visibility', 'hidden')
      .append('foreignObject')
      .attr('class', 'y-label')
      .attr('x', 50)
      .attr('y', -15)
      .attr('width', chartWidth - 10)
      .attr('height', 20)

    const circles = svg
      .selectAll('.y-point')
      .data(filteredData)
      .enter()
      .append('circle')
      .attr('class', '.y-point')
      .attr('cx', (d) => xScale(d.month))
      .attr('cy', (d, i) => yScale(d.value))
      .attr('r', (_, i) => (i === filteredData?.length - 1 ? 6 : 4))
      .attr('fill', '#4B1600')
      .attr('stroke', '#FFFFFF')
      .attr('stroke-width', (_, i) => (i === filteredData?.length - 1 ? 3 : 1))

    const TOOLTIP_FLIP_HEIGHT = 25

    const offsetForYScale = (yValue) => {
      const scaleY = yScale(yValue)
      const offset = scaleY < TOOLTIP_FLIP_HEIGHT ? 27 : -10
      return yScale(yValue) + offset
    }

    const getTrianglePath = (yValue) => {
      const scaleY = yScale(yValue)
      const triangleHeight = 5
      const triangleWidth = scaleY < TOOLTIP_FLIP_HEIGHT ? 8 : 10
      const centerX = 0
      return scaleY < TOOLTIP_FLIP_HEIGHT
        ? `M${centerX - triangleWidth / 2},${triangleHeight} L${centerX},0 L${centerX + triangleWidth / 2},${triangleHeight} Z`
        : `M${centerX - triangleWidth / 2}, 0 L${centerX},${triangleHeight} L${centerX + triangleWidth / 2}, 0 Z`
    }

    const getTriangleOffset = (yValue) => {
      const scaleY = yScale(yValue)
      return scaleY < TOOLTIP_FLIP_HEIGHT ? -20 : -2
    }

    const filteredSectionedData = sectionedData?.filter((d) => d?.isVisible)

    const isDisplayInitialTooltip = (d: PropTypes['data'][0]) => {
      return (
        filteredSectionedData?.filter((datum) => datum?.month === d?.month && datum?.value === d?.value)?.length > 0
      )
    }

    const initialTooltips = svg
      .selectAll('.tooltip-initial')
      .data(filteredData)
      .enter()
      .append('g')
      .attr('class', 'tooltip-initial')
      .attr('transform', (d) => `translate(${xScale(d.month)},${offsetForYScale(d.value)})`)
      .style('opacity', (d) => (isDisplayInitialTooltip(d) ? 1 : 0))

    initialTooltips
      .append('text')
      .attr('x', 0)
      .attr('y', -8)
      .attr('dy', '.35em')
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', 'bold')
      .attr('fill', '#4B1600')
      .text((d) => (showLabels ? d.label : d.value))

    initialTooltips
      .insert('rect', 'text')
      .attr('x', function () {
        const textWidth = this.nextSibling.getBBox().width
        return -(textWidth / 2) - 5
      })
      .attr('y', -16)
      .attr('width', function () {
        const textWidth = this.nextSibling.getBBox().width
        return textWidth + 10
      })
      .attr('height', 16)
      .attr('fill', 'white')
      .attr('rx', 5)
      .attr('ry', 5)

    initialTooltips
      .append('path')
      .attr('d', (d) => {
        return getTrianglePath(d.value)
      })
      .attr('fill', 'white')
      .attr('transform', (d) => `translate(0, ${getTriangleOffset(d.value)})`)

    svg.on('touchstart touchmove', (event) => {
      // event.preventDefault()
      const touch = event.touches[0]
      const touchX = touch.clientX - (svgRef.current?.getBoundingClientRect().left ?? 0)

      // Find the nearest data point
      const closestData = data.reduce((a, b) =>
        Math.abs(xScale(a.month) - touchX) < Math.abs(xScale(b.month) - touchX) ? a : b
      )

      const closestDataX = xScale(closestData.month)

      if (closestData?.isVisible) {
        if (closestData?.month !== lastDataRef?.current?.month) {
          lightImpactVibrate()
          lastDataRef.current = closestData
        }
        // Update the drag line position to snap to the nearest point
        topDragLine
          .attr('x1', closestDataX + 0.5)
          .attr('x2', closestDataX + 0.5)
          .attr('y1', 4)
          .attr('y2', 34)
          .style('visibility', 'visible')

        dragLine
          .attr('x1', closestDataX)
          .attr('x2', closestDataX)
          .attr('y1', 0)
          .attr('y2', chartHeight)
          .style('visibility', 'visible')

        // Hide initial initialTooltips and show the tooltip for the closest point
        initialTooltips.style('opacity', 0)

        circles
          .attr('stroke', '#FFFFFF')
          .attr('stroke-width', (d, i) => (d.month === closestData.month ? 3 : 1))
          .attr('r', (d, i) => (d.month === closestData.month ? 6 : 4))

        initialTooltips.style('opacity', (d) => (d.month === closestData.month ? 1 : 0))

        const closestDataPerformanceTag = closestData?.tag
        const monthYearString =
          ranksType === 'TRAILING' && closestData.month === lastMonth
            ? 'Today'
            : `${convertEpochToDate(closestData.month, '%b %Y')}`

        const formLabelId = `form-label-${Date.now()}`
        const seperatorId = `seperator-${Date.now()}`
        const monthYearId = `month-year-${Date.now()}`

        monthText
          .style('visibility', 'visible')
          .html(
            `<div style="font-size: 12px; display: flex; gap: 4px; padding-bottom: 4px;">
           <div id=${formLabelId} style="font-weight: 700">${getPerformanceTagLabel(closestDataPerformanceTag)}</div>
           <div id=${seperatorId} style="color: rgba(0, 0, 0, 0.30)"> | </div> 
           <div id=${monthYearId}>${monthYearString} </div>
           </div>`
          )
          .attr('y', 2)

        const formLabel = document.getElementById(formLabelId)?.clientWidth
        const seperator = document.getElementById(seperatorId)?.clientWidth
        const monthYear = document.getElementById(monthYearId)?.clientWidth
        const totalWidth = formLabel + seperator + monthYear
        const boxWidth = totalWidth + 30

        let offset = 0
        if (closestDataX > chartWidth * 0.86) offset = boxWidth / 2 - 15
        else if (closestDataX > chartWidth * 0.8) offset = boxWidth / 2 - 40

        monthText.attr('x', closestDataX - totalWidth / 2 - offset - 5)

        monthTooltip
          .style('visibility', 'visible')
          .attr('x', closestDataX - boxWidth / 2 - offset)
          .attr('y', 0)
          .attr('fill', getPerformanceTagColor(closestDataPerformanceTag))
          .attr('width', boxWidth)

        if (onRankChange && closestData?.isVisible)
          onRankChange(closestData.value, closestData.month === lastMonth ? 'Today' : closestData.month, true)
      }
    })

    svg.on('touchend', () => {
      dragLine.style('visibility', 'hidden')
      topDragLine.style('visibility', 'hidden')
      initialTooltips.style('opacity', (d) => (isDisplayInitialTooltip(d) ? 1 : 0))

      monthTooltip.style('visibility', 'hidden')
      monthText.style('visibility', 'hidden')

      circles
        .attr('stroke', '#FFFFFF')
        .attr('stroke-width', (d, i) => (i === filteredData.length - 1 ? 3 : 1))
        .attr('r', (d, i) => (i === filteredData.length - 1 ? 6 : 4))

      onRankChange(lastValue, 'Today', false)
    })

    return () => {
      d3.select(svgRef.current).selectAll('*').remove()
      d3.select(topSVGRef.current).selectAll('*').remove()
    }
  }, [data, width, showLabels])

  return (
    <div
      className='flex flex-col'
      role='img'
      aria-label={`Rank trend graph for ${ranksType === 'ROLLING' ? 'rolling' : 'trailing'} returns`}
    >
      <div className='mb-[-1px]' aria-hidden='true'>
        <svg ref={topSVGRef}></svg>
      </div>
      <div aria-hidden='true'>
        <svg style={{ borderRadius: '12px', border: '0.5px solid #8E93933A', overflow: 'visible' }} ref={svgRef}></svg>
      </div>
    </div>
  )
}

export default NewRankGraph
